import React, { useState, useEffect } from "react";
import Layout from "../../Layout";
import { Link } from "react-router-dom";
import { IoArrowBackOutline } from "react-icons/io5";
import { Button, Checkbox, Select, Textarea } from "../../components/Form";
import { BiChevronDown, BiPlus } from "react-icons/bi";
import { medicineData, memberData, servicesData } from "../../components/Datas";
import { MedicineDosageTable } from "../../components/Tables";
import { toast } from "react-hot-toast";
import MedicineDosageModal from "../../components/Modals/MedicineDosage";
import { FaTimes } from "react-icons/fa";
import Uploader from "../../components/Uploader";
import { HiOutlineCheckCircle } from "react-icons/hi";
import Modal from "../../components/Modals/Modal";
import { useForm, Controller } from "react-hook-form";

// Initial symptoms list
const initialSymptoms = [
  { id: 1, name: "Headache" },
  { id: 2, name: "Fever" },
  { id: 3, name: "Cough" },
  { id: 4, name: "Fatigue" },
  { id: 5, name: "Nausea" },
  { id: 6, name: "+ Add another symptom" }
];

const doctorsData = memberData.map((item) => {
  return {
    id: item.id,
    name: item.title,
  };
});

function NewMedicalRecode({ isOpen: isMRMO, closeModal, patientId }) {
  const { control, handleSubmit, setValue, watch, register } = useForm({
    defaultValues: {
      doctor: doctorsData[0],
      complains: "",
      diagnosis: "",
      vitalSigns: "",
      selectedSymptom: initialSymptoms[0],
      treatments: servicesData.map(item => ({
        id: item.id,
        name: item.name,
        checked: false,
        price: item.price
      }))
    }
  });

  const [doctors, setDoctors] = useState(doctorsData[0]);
  const [isOpen, setIsOpen] = useState(false);
  
  // State for custom symptom input
  const [symptoms, setSymptoms] = useState(initialSymptoms);
  const [showCustomSymptomInput, setShowCustomSymptomInput] = useState(false);
  const [customSymptom, setCustomSymptom] = useState("");
  
  // State for custom treatment
  const [treatments, setTreatments] = useState(
    servicesData.map((item) => ({
      id: item.id,
      name: item.name,
      checked: false,
      price: item.price,
    }))
  );
  const [showCustomTreatmentInput, setShowCustomTreatmentInput] = useState(false);
  const [customTreatment, setCustomTreatment] = useState("");

  // Watch selected symptom to handle "+ Add another symptom" selection
  const selectedSymptom = watch("selectedSymptom");

  // Handle symptom selection
  const handleSymptomChange = (symptom) => {
    if (symptom.name === "+ Add another symptom") {
      setShowCustomSymptomInput(true);
      setCustomSymptom(""); // Clear the input when switching to custom input
    } else {
      setValue("selectedSymptom", symptom);
    }
  };

  // Add custom symptom
  const handleAddCustomSymptom = () => {
    if (customSymptom.trim()) {
      const newSymptom = {
        id: symptoms.length + 1,
        name: customSymptom.trim()
      };
      
      // Add to symptoms list (excluding the "+ Add another" option)
      const updatedSymptoms = [
        ...symptoms.filter(s => s.name !== "+ Add another symptom"),
        newSymptom,
        { id: symptoms.length + 2, name: "+ Add another symptom" }
      ];
      
      setSymptoms(updatedSymptoms);
      setValue("selectedSymptom", newSymptom);
      setCustomSymptom("");
      setShowCustomSymptomInput(false);
    }
  };

  // Handle custom treatment addition
  const handleAddCustomTreatment = () => {
    if (customTreatment.trim()) {
      const newTreatment = {
        id: treatments.length + 1,
        name: customTreatment.trim(),
        checked: true,
        price: 0 // Default price
      };
      
      const updatedTreatments = [...treatments, newTreatment];
      setTreatments(updatedTreatments);
      
      // Update in react-hook-form
      setValue("treatments", updatedTreatments);
      
      setCustomTreatment("");
      setShowCustomTreatmentInput(false);
    }
  };

  // Handle treatment checkbox change
  const handleTreatmentChange = (id, checked) => {
    const updatedTreatments = treatments.map(treatment => 
      treatment.id === id ? { ...treatment, checked } : treatment
    );
    
    setTreatments(updatedTreatments);
    setValue("treatments", updatedTreatments);
  };

  const onSubmit = (data) => {
    console.log("Form Data:", data);
    
    // Filter selected treatments
    const selectedTreatments = data.treatments.filter(t => t.checked);
    
    // Create submission data
    const submissionData = {
      ...data,
      selectedTreatments
    };
    
    console.log("Submission Data:", submissionData);
    toast.error("This feature is not available yet");
  };

  return (
    <Modal
      isOpen={isMRMO}
      closeModal={closeModal}
      title="New Medical Record"
    >
      {
        isOpen && (
          <MedicineDosageModal
            isOpen={isOpen}
            closeModal={() => { setIsOpen(false); }}
          />
        )
      }
      <div className="w-full">
        <form onSubmit={handleSubmit(onSubmit)} className='my-8 flex flex-col items-start gap-6'>
          {/* tab panel */}
          <div
            data-aos='fade-left'
            data-aos-duration='1000'
            data-aos-delay='100'
            data-aos-offset='200'
            className='col-span-12 w-full rounded-xl border-[1px] border-border bg-white p-6 lg:col-span-8'
          >
            <div className='flex w-full flex-col gap-5'>
              {/* doctor */}
              <div className='flex w-full flex-col gap-3'>
                <p className='text-sm text-black'>Doctor</p>
                <Controller
                  name="doctor"
                  control={control}
                  render={({ field }) => (
                    <Select
                      selectedPerson={field.value}
                      setSelectedPerson={(doctor) => {
                        field.onChange(doctor);
                        setDoctors(doctor);
                      }}
                      datas={doctorsData}
                    >
                      <div className='flex-btn w-full rounded-lg border border-border p-4 text-sm font-light text-textGray focus:border focus:border-subMain'>
                        {field.value.name} <BiChevronDown className='text-xl' />
                      </div>
                    </Select>
                  )}
                />
              </div>
              
              {/* Symptoms input */}
              <div className='flex w-full flex-col gap-3'>
                <p className='text-sm text-black'>Symptoms</p>
                {showCustomSymptomInput ? (
                  <div className='flex w-full items-center gap-2'>
                    <input
                      type="text"
                      value={customSymptom}
                      onChange={(e) => setCustomSymptom(e.target.value)}
                      placeholder="Enter custom symptom"
                      className='w-full rounded-lg border border-border p-4 text-sm font-light focus:border focus:border-subMain'
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleAddCustomSymptom();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAddCustomSymptom}
                      className='flex-colo rounded-lg bg-subMain px-4 py-4 text-white'
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCustomSymptomInput(false);
                        setCustomSymptom("");
                        setValue("selectedSymptom", symptoms[0]);
                      }}
                      className='flex-colo h-12 w-12 rounded-full bg-white'
                    >
                      <FaTimes className='text-red-500' />
                    </button>
                  </div>
                ) : (
                  <Controller
                    name="selectedSymptom"
                    control={control}
                    render={({ field }) => (
                      <Select
                        selectedPerson={field.value}
                        setSelectedPerson={handleSymptomChange}
                        datas={symptoms}
                      >
                        <div className='flex-btn w-full rounded-lg border border-border p-4 text-sm font-light text-textGray focus:border focus:border-subMain'>
                          {field.value.name} <BiChevronDown className='text-xl' />
                        </div>
                      </Select>
                    )}
                  />
                )}
              </div>
              
              {/* complains */}
              <Controller
                name="complains"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    label='Complains'
                    color={true}
                    rows={3}
                    placeholder={"Bad breath, toothache, ...."}
                  />
                )}
              />
              
              {/* Diagnosis */}
              <Controller
                name="diagnosis"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    label='Diagnosis'
                    color={true}
                    rows={3}
                    placeholder={"Gingivitis, Periodontitis, ...."}
                  />
                )}
              />
              
              {/* Vital Signs */}
              <Controller
                name="vitalSigns"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    label='Vital Signs'
                    color={true}
                    rows={3}
                    placeholder={"Blood pressure, Pulse, ...."}
                  />
                )}
              />
              
              {/* Treatment */}
              <div className='flex w-full flex-col gap-4'>
                <p className='text-sm text-black'>Treatment</p>
                <div className='grid gap-6 pb-6 xs:grid-cols-2 md:grid-cols-3'>
                  {treatments.map((item) => (
                    <Controller
                      key={item.id}
                      name={`treatments[${item.id}].checked`}
                      control={control}
                      defaultValue={item.checked}
                      render={({ field }) => (
                        <Checkbox
                          label={item.name}
                          checked={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.checked);
                            handleTreatmentChange(item.id, e.target.checked);
                          }}
                          name={`treatment-${item.id}`}
                        />
                      )}
                    />
                  ))}
                </div>
                
                {/* Add custom treatment button */}
                {!showCustomTreatmentInput && (
                  <button
                    type="button"
                    onClick={() => setShowCustomTreatmentInput(true)}
                    className='flex-rows w-full gap-2 rounded-lg border border-dashed border-subMain py-4 text-sm text-subMain'
                  >
                    <BiPlus /> Add Another Treatment
                  </button>
                )}
                
                {/* Custom treatment input */}
                {showCustomTreatmentInput && (
                  <div className='flex w-full items-center gap-2'>
                    <input
                      type="text"
                      value={customTreatment}
                      onChange={(e) => setCustomTreatment(e.target.value)}
                      placeholder="Enter custom treatment"
                      className='w-full rounded-lg border border-border p-4 text-sm font-light focus:border focus:border-subMain'
                    />
                    <button
                      type="button"
                      onClick={handleAddCustomTreatment}
                      className='flex-colo rounded-lg bg-subMain px-4 py-4 text-white'
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCustomTreatmentInput(false);
                        setCustomTreatment("");
                      }}
                      className='flex-colo h-12 w-12 rounded-full bg-white'
                    >
                      <FaTimes className='text-red-500' />
                    </button>
                  </div>
                )}
              </div>
              
              {/* medicine */}
              <div className='mb-6 flex w-full flex-col gap-4'>
                <p className='text-sm text-black'>Medicine</p>
                <div className='w-full overflow-x-scroll'>
                  <MedicineDosageTable
                    data={medicineData?.slice(0, 3)}
                    functions={{
                      delete: (id) => {
                        toast.error("This feature is not available yet");
                      },
                    }}
                    button={true}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(true);
                  }}
                  className='flex-rows w-full gap-2 rounded-lg border border-dashed border-subMain py-4 text-sm text-subMain'
                >
                  <BiPlus /> Add Medicine
                </button>
              </div>
              
              {/* attachment */}
              <div className='flex w-full flex-col gap-4'>
                <p className='text-sm text-black'>Attachments</p>
                <div className='grid w-full gap-4 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4'>
                  {[1, 2, 3, 4].map((_, i) => (
                    <div key={i} className='relative w-full'>
                      <img
                        src={`https://placehold.it/300x300?text=${i}`}
                        alt='patient'
                        className='w-full rounded-lg object-cover md:h-40'
                      />
                      <button
                        type="button"
                        onClick={() =>
                          toast.error("This feature is not available yet.")
                        }
                        className='flex-colo absolute -right-1 -top-1 h-8 w-8 rounded-full bg-white'
                      >
                        <FaTimes className='text-red-500' />
                      </button>
                    </div>
                  ))}
                </div>
                <Uploader setImage={{}} />
              </div>
              
              {/* submit */}
              <Button
                type="submit"
                label={"Save"}
                Icon={HiOutlineCheckCircle}
              />
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default NewMedicalRecode;
